
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Namespace } from 'enums/namespaces';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import Layout from '../../components/Layout';
import styles from './index.module.scss';
import Head from 'next/head';
import { basicSEO } from 'helpers/jsx';
import { useAuth, useNotificationContext } from 'context';
import { getSharedApiClient } from 'api/client';
import { SendEmailRequest } from 'typings/api/send-email-request';
import { StaticPathParams } from 'typings/staticPaths';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { createRedirectIfNeeded } from 'helpers';

interface IFormValues {
  email: string;
  message: string;
}

interface Props {
  countryCode: string;
}

const Authorship: NextPage<Props> = ({ countryCode }) => {
  const EMAIL_MAX_LENGTH = 100;
  const MESSAGE_MAX_LENGTH = 2000;
  const { user } = useAuth();
  const { t } = useTranslation(Namespace.Authorship);
  const { createNotification } = useNotificationContext();
  const [wasSendSuccess, setWasSendSuccess] = React.useState<boolean>(false);
  const [initialValues, setInitialValues] = React.useState<IFormValues>({
    email: '',
    message: '',
  });

  const validationSchema = () => {
    const field_required = t('authorship_field_required');
    const max_length = t('authorship_max_length');
    const invalid_email = t('authorship_email_invalid');

    const formSchema = Yup.object().shape({
      email: Yup.string()
        .email(invalid_email)
        .max(EMAIL_MAX_LENGTH, `${max_length} ${EMAIL_MAX_LENGTH}`)
        .required(field_required),
      message: Yup.string().max(MESSAGE_MAX_LENGTH, `${max_length} ${MESSAGE_MAX_LENGTH}`).required(field_required),
    });

    return formSchema;
  };

  const onSubmit = async (values: IFormValues, { resetForm }: FormikHelpers<IFormValues>) => {
    try {
      const emailIn: SendEmailRequest = {
        subject: 'Authorship claim',
        sender: values.email,
        body: values.message,
        type: 'COPYRIGHT',
      };

      if (user) {
        delete emailIn.sender;
      }

      await getSharedApiClient().sendEmail(emailIn);

      // Reset form & show success message on OK send
      createNotification({ title: t('authorship_email') });
      setWasSendSuccess(true);
      resetForm();

      setTimeout(() => {
        setWasSendSuccess(false);
      }, 2000);
    } catch (e) {
      createNotification({ title: t('authorship_email_fail') });
      setWasSendSuccess(false);
    }
  };

  React.useEffect(() => {
    if (user && user.email) {
      setInitialValues({ email: user.email, message: '' });
    }
  }, [user]);

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:authorship_title'), t('seo:authorship_description'))}</Head>
      <main className={styles.authorship}>
        <div className={styles.sectionWrapper}>
          <div className={styles.heading}>
            <h1>{t('menu_authorship')}</h1>
          </div>
          <div>
            <Trans i18nKey={'authorship:authorship_perex'} components={[<p />, <br />]} />
          </div>
        </div>
        <div className={classNames(styles.sectionWrapper, styles.form, styles.withBackground, styles.reverse)}>
          <div className={styles.heading}>
            <h2>{t('authorship_title')}</h2>
          </div>
          <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema()}
            enableReinitialize
          >
            {({ values, setFieldValue, isSubmitting, touched, errors }) => {
              return (
                <Form autoComplete="off">
                  {!user && (
                    <label>
                      {t('authorship_your_email')}
                      <input
                        type="text"
                        className={classNames(styles.email, {
                          error: touched.message && errors.message,
                        })}
                        name="email"
                        id="email"
                        value={values.email}
                        onChange={(e) => setFieldValue('email', e?.target?.value)}
                      />

                      {touched.email && errors.email && <span className="d-block">{errors.email}</span>}
                    </label>
                  )}

                  <label>
                    {t('authorship_message_for_us')}
                    <textarea
                      maxLength={2000}
                      className={classNames(styles.question, 'required', {
                        error: touched.message && errors.message,
                      })}
                      id="question"
                      value={values.message}
                      onChange={(e) => setFieldValue('message', e?.target?.value)}
                    ></textarea>
                    {touched.message && errors.message && <span className="d-block">{errors.message}</span>}
                  </label>
                  <div className={styles.controls} id="controls">
                    <p id="remaining" className={styles.remaining}>
                      {values.message?.length || 0} / {MESSAGE_MAX_LENGTH}
                    </p>

                    <button
                      type="submit"
                      className={classNames(styles.btn, 'btn', 'btn-primary')}
                      disabled={isSubmitting || wasSendSuccess}
                    >
                      {isSubmitting && (
                        <div className={classNames('spinner-border', styles.spinner)} role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      )}

                      {wasSendSuccess ? t('authorship_email') : t('authorship_send_form')}
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className={classNames(styles.sectionWrapper, styles.provider, styles.withBackground)}>
          <div className={styles.heading}>
            <h2>{t('authorship_provider')}</h2>
          </div>
          <div className={styles.info}>
            <address>
              Dramox s.r.o.
              <br />
              Bucharova 2928/14a, Stodůlky
              <br />
              IČO: 09222103
            </address>
          </div>
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { apiClient, countryCode, locale } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  return {
    props: { countryCode },
  };
}

export default Authorship;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/authorship/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  